import {
  IFXAccordionHeaderIconButton,
  isEmptyOrNull,
} from "@ifx-react/ifx-react-core";
import * as queryString from "query-string";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { USER_MANUAL_ACCESS_URL } from "../../../const/common/endpoints";
import { ADMIN_TASK_NAMES } from "../../../const/portal/PortalTaskConsts";
import { useUserTaskAnyGranted } from "../../../hooks/portal/userSecurityHooks";
import { LoadHelpLink } from "../../../store/actions/common/actionCreators";
import { useGitBookManager } from "../../../util/common/GitBookWindowManager";

export const HelpLink = React.memo(({ currentPage }) => {
  const dispatch = useDispatch();

  const { openGitBookHelp, logoutGitBook } = useGitBookManager();

  const helpLinkData = useSelector(
    state => state.CommonReducer.HelpLinkReducer
  );

  const carrierId = useSelector(
    state => state.PortalReducer.UserContextReducer.selectedAirline?.id || null
  );

  const { employerType } = useSelector(
    state => state.PortalReducer.UserContextReducer.userInfo
  );

  const isAdmin = useUserTaskAnyGranted(ADMIN_TASK_NAMES);

  useEffect(() => {
    if (carrierId != null) {
      dispatch(
        LoadHelpLink.REQUEST({
          carrierId,
          userType: employerType,
        })
      );
    } else {
      dispatch(LoadHelpLink.RESET());
    }
  }, [carrierId]);

  const viewHelpLink = useMemo(() => {
    const filterData = helpLinkData.filter(
      item =>
        !currentPage?.hideHelpLinkMenu &&
        item?.module?.toLowerCase() ===
          currentPage?.helpModuleTitle?.toLowerCase() &&
        item?.function?.toLowerCase() ===
          currentPage?.helpFunctionTitle?.toLowerCase()
    );

    if (filterData.length === 1) {
      return filterData[0];
    } else if (filterData.length > 1) {
      return filterData.find(item => {
        if (item.carrierId && item.userType) {
          return item;
        } else if (item.carrierId && isEmptyOrNull(item.userType)) {
          return item;
        } else if (isEmptyOrNull(item.carrierId) && item.userType) {
          return item;
        } else {
          return item;
        }
      });
    }
  }, [helpLinkData, currentPage]);

  const helpAccessUrl = useMemo(() => {
    if (isEmptyOrNull(viewHelpLink?.externalURL)) return null;

    const apiURL = USER_MANUAL_ACCESS_URL;
    const params = {
      targetUrl: viewHelpLink.externalURL,
      module: viewHelpLink.module,
      function: viewHelpLink.function,
      isAdmin: isAdmin,
    };

    return `${apiURL}?${queryString.stringify(params)}`;
  }, [viewHelpLink, isAdmin]);

  const handleHelpClick = useCallback(
    e => {
      e.preventDefault();
      if (helpAccessUrl) {
        openGitBookHelp(helpAccessUrl);
      }
    },
    [helpAccessUrl]
  );

  useEffect(() => {
    return () => {
      logoutGitBook();
    };
  }, [logoutGitBook]);

  useEffect(() => {
    if (isEmptyOrNull(viewHelpLink?.externalURL)) return;

    // Best-effort: log out of GitBook when the user closes the app tab
    const handleBeforeUnload = () => {
      logoutGitBook({ closeAfterLogout: false });
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [viewHelpLink?.externalURL]);

  return viewHelpLink?.externalURL ? (
    <div className="page-help-link">
      <a href={helpAccessUrl} onClick={handleHelpClick} rel="noreferrer">
        <IFXAccordionHeaderIconButton
          icon="info"
          name="Click here for help on this page"
          iconSource="svg"
          size="sm"
          tooltipProps={{
            placement: "left",
          }}
        />
      </a>
    </div>
  ) : null;
});
